import React from 'react';
import { Star } from '@phosphor-icons/react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';
import { SrOnly } from './SrOnly';

/**
 * Rating, the star rating, in both a read-only and an input mode.
 *
 * Source selectors: `system.css` `.dsk-rating`, `--sm`, `--lg`, `--input`,
 * `__stars`, `__track`, `__fill`, `__value`, `__count`, `__sales`, plus
 * `.dsk-sr-only` for the input mode's real radios.
 *
 * WHY THIS EXISTS. The app carries seven copy-pasted `renderStars` functions
 * and one of them divides the value by 20 instead of 5, so every rating on that
 * screen renders at a fifth of its true length. This component therefore takes
 * an EXPLICIT `outOf` and defaults it to 5. Nothing here guesses a denominator.
 *
 * Doctrine enforced (README section 8, the one icon nuance):
 * - The earned portion is FILL, the remainder is REGULAR. Fill is the active
 *   weight, and a rating is the one place a partial fill is meaningful.
 * - Stars are INK, never yellow. Gold would be the only saturated colour on the
 *   page and it would outrank the accent, which is the system's state colour.
 * - The value is encoded as a CLIPPED WIDTH, not a rounded star count, so 4.9
 *   shows four full stars and a nearly full fifth. A half value is honest.
 * - Count and sales are the corroboration, not the headline, so they stay muted
 *   and are never the only thing carrying the meaning.
 *
 * The `.dsk-rating__fill` rule is `width: calc(var(--dsk-rating) / 5 * 100%)`,
 * a fixed denominator of 5. So a non-5 `outOf` is NORMALISED to a 5-point scale
 * before it reaches the custom property, and five star glyphs are always drawn.
 * The width still lands at `value / outOf`, exactly right, and no CSS is forked
 * to support a scale the library does not have.
 *
 *   <Rating value={4.9} count={1204} sales={842} />
 *   <Rating value={87} outOf={100} />
 *   <Rating input name="rate-seller" value={stars} onChange={setStars} />
 */
const TRACK_STARS = 5;

function formatValue(value) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function sizeClassFor(size) {
  return { sm: 'dsk-rating--sm', md: null, lg: 'dsk-rating--lg' }[size] || null;
}

export const Rating = React.forwardRef(function Rating(
  {
    value,
    outOf = 5,
    count,
    sales,
    showValue = true,
    size = 'md',
    input = false,
    name,
    defaultValue,
    onChange,
    label,
    countLabel = 'reviews',
    salesLabel = 'sales',
    className,
    ...rest
  },
  ref,
) {
  const uid = React.useId();
  const groupName = name || `${uid}-rating`;
  const isControlled = value !== undefined;
  const [internal, setInternal] = React.useState(defaultValue ?? 0);
  const current = input && !isControlled ? internal : value ?? 0;

  /* ---- Input mode -------------------------------------------------------
     Source order runs HIGH to LOW and the row is reversed by the CSS, which is
     what lets a general sibling selector light the star you are on plus every
     star below it. Reordering this breaks the hover and checked states. Both
     glyph weights are in the DOM and the CSS chooses which is visible, because
     a stylesheet cannot swap a class. */
  if (input) {
    const stars = [];
    for (let n = outOf; n >= 1; n -= 1) stars.push(n);

    return (
      <span
        ref={ref}
        role="radiogroup"
        aria-label={label || `Rating out of ${outOf}`}
        className={cn('dsk-rating', 'dsk-rating--input', sizeClassFor(size), className)}
        {...rest}
      >
        {stars.map((n) => (
          <React.Fragment key={n}>
            <input
              className="dsk-sr-only"
              type="radio"
              name={groupName}
              id={`${uid}-${n}`}
              value={n}
              checked={current === n}
              onChange={() => {
                if (!isControlled) setInternal(n);
                if (onChange) onChange(n);
              }}
            />
            <label htmlFor={`${uid}-${n}`}>
              <SrOnly>
                {n} {n === 1 ? 'star' : 'stars'}
              </SrOnly>
              <Icon as={Star} />
              <Icon as={Star} active />
            </label>
          </React.Fragment>
        ))}
      </span>
    );
  }

  /* ---- Unrated ------------------------------------------------------------
     NO RATING IS NOT A RATING OF ZERO, and five empty stars beside a "0" is
     the exact shape of a terrible one. The card page listing table printed
     that under every new seller, so the buy box read as "reviewed, and found
     worthless" for someone nobody had reviewed at all.

     A zero WITH reviews behind it is a real rating and still draws stars; only
     the absence of any rating takes this branch. `sales` survives it, because
     completed orders are a fact that does not depend on anyone leaving a
     review — and for a new seller it is the only corroboration there is. */
  const unrated = !input && (value == null || (value === 0 && (count == null || count === 0)));
  if (unrated) {
    return (
      <span
        ref={ref}
        className={cn('dsk-rating', 'dsk-rating--unrated', sizeClassFor(size), className)}
        {...rest}
      >
        <span className="dsk-rating__count">No ratings yet</span>
        {sales !== undefined && sales !== null ? (
          <span className="dsk-rating__sales">
            {Number(sales).toLocaleString('en-ZA')} {salesLabel}
          </span>
        ) : null}
      </span>
    );
  }

  /* ---- Read-only mode --------------------------------------------------- */
  const safeOutOf = outOf > 0 ? outOf : 5;
  const clamped = Math.min(Math.max(current || 0, 0), safeOutOf);
  const normalised = (clamped / safeOutOf) * TRACK_STARS;

  const track = [];
  for (let i = 0; i < TRACK_STARS; i += 1) track.push(i);

  return (
    <span
      ref={ref}
      role="img"
      aria-label={label || `Rated ${formatValue(clamped)} out of ${safeOutOf}`}
      className={cn('dsk-rating', sizeClassFor(size), className)}
      style={{ '--dsk-rating': normalised }}
      {...rest}
    >
      <span className="dsk-rating__stars" aria-hidden="true">
        <span className="dsk-rating__track">
          {track.map((i) => (
            <Icon key={i} as={Star} />
          ))}
        </span>
        <span className="dsk-rating__fill">
          {track.map((i) => (
            <Icon key={i} as={Star} active />
          ))}
        </span>
      </span>
      {showValue ? <span className="dsk-rating__value">{formatValue(clamped)}</span> : null}
      {count !== undefined && count !== null ? (
        <span className="dsk-rating__count">
          {Number(count).toLocaleString('en-ZA')} {countLabel}
        </span>
      ) : null}
      {sales !== undefined && sales !== null ? (
        <span className="dsk-rating__sales">
          {Number(sales).toLocaleString('en-ZA')} {salesLabel}
        </span>
      ) : null}
    </span>
  );
});
Rating.displayName = 'Rating';

export default Rating;
